// payload-model.ts — the TypeScript side of jobrunner.RunJobEvent (godev
// jobrunner/jobrunner.go), UI-free so it can be unit-tested without a renderer
// (same split as monitor-config.ts / monitor-config.test.ts).
//
// PayloadControl edits job.payload_json structurally and deliberately exposes no
// raw JSON, so a field this model does not know about is a field NOBODY can set
// through the UI. That is how `backend` and `executionUser` — the run-as pair —
// stayed unreachable while running three pgbackrest backups in prod
// (FEATURE-20260822-ui-job-form-exposes-run-as).
//
// Keep in sync with jobrunner.RunJobEvent.

/** The whole RunJobEvent shape. Fields the control does not render are declared
 *  anyway, because the contract is that editing a job PRESERVES them untouched —
 *  and a field that is not in the type is a field nobody remembered to preserve. */
export type JobPayload = {
  command?: string[];
  cwd?: string;
  runDirect?: boolean;
  captureConsole?: boolean | null;
  /** os-exec (default) | systemd-transient — how the runner launches the process. */
  backend?: string;
  /** the Unix user a systemd-transient job runs as; ignored by the os-exec path. */
  executionUser?: string;
  /** the job's OWN environment override; the scheduler merges it over the execution
   *  user's worker.job_env at dispatch. Ignored by the os-exec path. */
  env?: Record<string, string>;
  /** systemd Restart= for a daemon job; the runner injects it per launch. */
  restartPolicy?: string;
  /** systemd RestartSec= — how long systemd waits before restarting. */
  restartDelaySeconds?: number;
  /** systemd TimeoutStopSec= — how long a stop or restart waits for the daemon to drain. */
  stopGraceSeconds?: number;
  // Not rendered: the process-contract override. It round-trips untouched.
  // FEATURE-20260801-job-kind-enum-replaces-payload-booleans owns that axis.
  selfReports?: boolean | null;
  // Anything else a future godev adds: also preserved, by the same rule.
  [key: string]: unknown;
};

/** RunJobEvent.Backend values (godev jobrunner/jobrunner.go `backendOsExec` etc). */
export const BACKEND_OS_EXEC = "os-exec";
export const BACKEND_SYSTEMD_TRANSIENT = "systemd-transient";

export const BACKEND_OPTIONS = [
  { value: BACKEND_OS_EXEC, text: "Direct — the job runner forks the process itself" },
  {
    value: BACKEND_SYSTEMD_TRANSIENT,
    text: "systemd transient — a privileged executor launches a unit",
  },
] as const;

/** Accepts the object the form holds, the JSON string the column returns, or junk. */
export function toPayload(v: unknown): JobPayload {
  if (v && typeof v === "object") return v as JobPayload;
  if (typeof v === "string") {
    try {
      const parsed: unknown = JSON.parse(v);
      if (parsed && typeof parsed === "object") return parsed as JobPayload;
    } catch {
      /* fall through to empty */
    }
  }
  return {};
}

/** Empty and "os-exec" mean the same thing to the runner ("empty or os-exec =
 *  today's behavior"), so the UI shows one value for both. */
export function backendOf(p: JobPayload): string {
  return p.backend?.trim() ? p.backend.trim() : BACKEND_OS_EXEC;
}

export function isSystemdTransient(p: JobPayload): boolean {
  return backendOf(p) === BACKEND_SYSTEMD_TRANSIENT;
}

/** Set the backend, writing NO key for os-exec. Writing "backend":"os-exec" would
 *  be a no-op change in meaning that churns the payload of every job the form
 *  touches, so the default stays absent. */
export function setBackend(p: JobPayload, backend: string): JobPayload {
  const next = { ...p };
  if (!backend || backend === BACKEND_OS_EXEC) delete next.backend;
  else next.backend = backend;
  return next;
}

/** Set the run-as user; a blank one drops the key rather than storing "".
 *  The value is KEPT when the backend goes back to os-exec — the same choice the
 *  control already makes for captureConsole when runDirect is off: disable the
 *  input, do not silently discard what someone typed. */
export function setExecutionUser(p: JobPayload, user: string): JobPayload {
  const next = { ...p };
  const trimmed = user.trim();
  if (trimmed) next.executionUser = trimmed;
  else delete next.executionUser;
  return next;
}

/** One entry in the Run as picker. `known` is false for a value the host's worker list
 *  does not contain — see runAsSelectOptions. */
export type RunAsOption = { value: string; text: string; known: boolean };

/** Build the Run as options from the logins continuum knows on the job's host.
 *
 *  The current value is ALWAYS present, even when it is not among the candidates: a
 *  select that cannot represent what is stored would silently rewrite the job's run-as
 *  identity the moment someone opened the form to change something else. An unknown
 *  value is marked so the UI can say why it looks odd rather than just dropping it. */
export function runAsSelectOptions(
  candidates: string[],
  current: string | undefined,
): RunAsOption[] {
  const seen = new Set<string>();
  const options: RunAsOption[] = [];
  for (const login of candidates) {
    const name = login.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    options.push({ value: name, text: name, known: true });
  }
  const now = current?.trim();
  if (now && !seen.has(now)) {
    options.unshift({ value: now, text: `${now} — not a known worker on this host`, known: false });
  }
  return options;
}

/** Why a systemd-transient job MUST name a user: the privileged executor rejects an
 *  empty ExecutionUser fail-closed (godev pkg/jobexecutor/policy.go). Catching it here
 *  turns a run that dies at launch into a form error. */
export function runAsIssue(p: JobPayload): string | undefined {
  if (isSystemdTransient(p) && !p.executionUser?.trim()) {
    return "A systemd transient job must name the user it runs as — the executor rejects an empty one.";
  }
  return undefined;
}

// ── Daemon jobs ─────────────────────────────────────────────────────────────
//
// A daemon job runs continuously: job.kind = "daemon" makes the runner's convergence
// loop (godev jobrunner/convergence.go) responsible for it, launching it through the
// privileged executor as a systemd transient unit and relaunching it when systemd's own
// restart policy has given up. It has no schedule — intent, not a timetable, is what
// keeps it running.
//
// Only two kinds are DEFINED by a person. The others exist and are written by the
// system: every job-runner registers itself as "service", and monitors create
// "monitor-probe". Offering those here would invite someone to hand-make a row the
// system believes it owns.
export const KIND_SCHEDULED = "scheduled";
export const KIND_DAEMON = "daemon";

export const KIND_OPTIONS = [
  { value: KIND_SCHEDULED, text: "Scheduled — runs when its schedule fires" },
  { value: KIND_DAEMON, text: "Daemon — runs continuously; the runner keeps it up" },
] as const;

export function isDaemonKind(kind: unknown): boolean {
  return String(kind ?? "").trim() === KIND_DAEMON;
}

/** The systemd Restart= values the executor accepts. It validates against this exact set
 *  and REJECTS anything else rather than passing it to systemd (godev
 *  pkg/jobexecutor/api.go), so the picker offers the set rather than free text. */
export const RESTART_POLICIES = [
  "always",
  "on-failure",
  "on-abnormal",
  "on-abort",
  "on-watchdog",
  "on-success",
  "no",
] as const;

/** What a daemon means by default: keep it running whatever it exits with. A person
 *  moving a supervisor program (autorestart=true) wants exactly this. */
export const DEFAULT_RESTART_POLICY = "always";

export const RESTART_POLICY_OPTIONS = [
  { value: "always", text: "always — restart whatever it exits with" },
  { value: "on-failure", text: "on-failure — restart on a non-zero exit" },
  { value: "on-abnormal", text: "on-abnormal — restart on a signal, timeout or watchdog" },
  { value: "on-abort", text: "on-abort — restart only on an uncaught signal" },
  { value: "on-watchdog", text: "on-watchdog — restart only on watchdog timeout" },
  { value: "on-success", text: "on-success — restart only on a clean exit" },
  { value: "no", text: "no — do not restart (the convergence loop still relaunches it)" },
] as const;

export function restartPolicyOf(p: JobPayload): string {
  return p.restartPolicy?.trim() ?? "";
}

/** Set the restart policy; a blank one drops the key so the payload does not carry an
 *  empty string the runner would have to interpret. */
export function setRestartPolicy(p: JobPayload, policy: string): JobPayload {
  const next = { ...p };
  const trimmed = policy.trim();
  if (trimmed) next.restartPolicy = trimmed;
  else delete next.restartPolicy;
  return next;
}

/** Set RestartSec=. Zero and nonsense drop the key — zero IS systemd's default, so
 *  writing it would carry a number that means "unset" and read as a decision. */
export function setRestartDelaySeconds(p: JobPayload, seconds: unknown): JobPayload {
  const next = { ...p };
  const n = Number(seconds);
  if (Number.isFinite(n) && n > 0) next.restartDelaySeconds = Math.floor(n);
  else delete next.restartDelaySeconds;
  return next;
}

/** Set TimeoutStopSec=. Zero and nonsense drop the key, leaving systemd's default. */
export function setStopGraceSeconds(p: JobPayload, seconds: unknown): JobPayload {
  const next = { ...p };
  const n = Number(seconds);
  if (Number.isFinite(n) && n > 0) next.stopGraceSeconds = Math.floor(n);
  else delete next.stopGraceSeconds;
  return next;
}

/** Sensible DEFAULTS for a newly created daemon, not a contract it is judged against.
 *  A daemon is launched through the executor because of its KIND, not because of this
 *  field -- the runner's convergence loop selects on kind alone and gates the executor
 *  per-host -- so backend here is a helpful pre-fill, and daemonIssues deliberately does
 *  not police it (dev's live e2e-daemon-test carries no backend key). The restart policy
 *  default matches what convergence.go applies for an unset one. Anything already chosen
 *  is kept. */
export function applyDaemonDefaults(p: JobPayload): JobPayload {
  let next = setBackend(p, BACKEND_SYSTEMD_TRANSIENT);
  if (!restartPolicyOf(next)) next = setRestartPolicy(next, DEFAULT_RESTART_POLICY);
  return next;
}

/** Everything wrong with this job AS A DAEMON, in the order someone would fix it.
 *  Empty for a job that is not a daemon — the knobs round-trip untouched there. */
export function daemonIssues(kind: unknown, p: JobPayload): string[] {
  if (!isDaemonKind(kind)) return [];
  const issues: string[] = [];
  // NOT backend. The runner's convergence loop selects daemons on KIND ALONE, and the
  // executor path is gated per-RUNNER (godev config.systemdBackendEnabled asks whether
  // that HOST has an executor socket), so payload.backend plays no part in the daemon
  // path at all. Dev's live e2e-daemon-test carries no backend key, and reporting that
  // as an error would flag a daemon that runs correctly today. The control still PINS
  // the select to systemd-transient for a new daemon -- a sensible default is not the
  // same thing as a rule.
  const policy = restartPolicyOf(p);
  if (policy && !RESTART_POLICIES.includes(policy as (typeof RESTART_POLICIES)[number])) {
    issues.push(
      `Restart policy "${policy}" is not one systemd accepts — the executor rejects it ` +
        `rather than passing it on.`,
    );
  }
  return issues;
}

// ── Environment ─────────────────────────────────────────────────────────────
//
// payload.env is the JOB'S OWN override. The scheduler resolves the full environment at
// dispatch (godev scheduler/jobenv.go) by layering this over the execution user's
// worker.job_env default, per key, and the runner layers its HOME/PATH formula under
// both. So what belongs here is only what is specific to THIS job — not the account's
// defaults, and never PATH unless this job genuinely needs a different one.
//
// It reaches the runner only on the systemd-transient path; the os-exec path ignores it.
//
// A supervisor program's `environment=` line is what this replaces, so the editor takes
// the shape a person already has in front of them: one KEY=VALUE per line, with a
// surrounding pair of quotes stripped, because supervisor writes KEY="value".

export function envOf(p: JobPayload): Record<string, string> {
  const raw = p.env;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "string") out[k] = v;
  }
  return out;
}

/** The env as editable text — one KEY=VALUE per line, insertion order preserved so
 *  opening a job and saving it back does not reshuffle what someone wrote. */
export function formatEnvText(p: JobPayload): string {
  return Object.entries(envOf(p))
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");
}

/** Strip ONE matched pair of surrounding quotes, so a line pasted straight out of a
 *  supervisor `environment=` clause means what it looks like. Inner quotes are data. */
function unquote(v: string): string {
  if (v.length >= 2 && (v[0] === '"' || v[0] === "'") && v[v.length - 1] === v[0]) {
    return v.slice(1, -1);
  }
  return v;
}

/** A name systemd will accept as an environment variable. Anything else is rejected by
 *  the executor's D-Bus Environment property, so catching it here turns a launch failure
 *  into a form error. */
const ENV_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

export type ParsedEnv = { env: Record<string, string>; badLines: string[] };

/** Parse KEY=VALUE lines. Blank lines and #comments are skipped; a line that is not a
 *  usable assignment is REPORTED rather than dropped, because silently discarding a line
 *  someone typed is how a daemon starts without the variable it needed. */
export function parseEnvText(text: string): ParsedEnv {
  const env: Record<string, string> = {};
  const badLines: string[] = [];
  for (const rawLine of text.split("\n")) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;
    const eq = line.indexOf("=");
    if (eq <= 0) {
      badLines.push(line);
      continue;
    }
    const name = line.slice(0, eq).trim();
    if (!ENV_NAME.test(name)) {
      badLines.push(line);
      continue;
    }
    env[name] = unquote(line.slice(eq + 1).trim());
  }
  return { env, badLines };
}

/** Set the env object; an empty one drops the key rather than storing {}, by the same
 *  rule as the other setters — an absent key and an empty object mean the same thing to
 *  the scheduler, and only one of them churns the payload of every job the form opens. */
export function setEnv(p: JobPayload, env: Record<string, string>): JobPayload {
  const next = { ...p };
  if (Object.keys(env).length) next.env = env;
  else delete next.env;
  return next;
}

/** What is wrong with the env text as typed, in the words the person needs. */
export function envIssues(badLines: string[]): string[] {
  if (!badLines.length) return [];
  return [
    `Not a KEY=VALUE assignment: ${badLines.join(", ")}. A name must start with a ` +
      `letter or underscore and hold only letters, digits and underscores.`,
  ];
}
